import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isMfaChallenge, type SessionContext } from '@mervonn/shared-types';
import { ApiError } from '../../lib/api/errors';
import { authApi } from './api';
import { Permissions } from './permissions';
import { tokenStore } from './tokenStore';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface LoginOutcome {
  mfaRequired: boolean;
  /** present when mfaRequired */
  mfaChallengeToken?: string;
}

export interface AuthContextValue {
  status: AuthStatus;
  session: SessionContext | null;
  permissions: Permissions | null;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  verifyMfa: (mfaChallengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** force a re-fetch of `/auth/me` (e.g. after a role change elsewhere) */
  refresh: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_QUERY_KEY = ['auth', 'session'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasSession, setHasSession] = useState(() => tokenStore.hasSession());

  useEffect(() => tokenStore.subscribe(() => setHasSession(tokenStore.hasSession())), []);

  const sessionQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => authApi.sessionContext(signal),
    enabled: hasSession,
    retry: false,
    staleTime: 5 * 60_000,
  });

  // A hard auth failure on /auth/me means the stored refresh token is also
  // dead — drop it so the app shows the login screen instead of spinning.
  useEffect(() => {
    if (sessionQuery.error instanceof ApiError && sessionQuery.error.isAuthFailure) {
      tokenStore.clear();
    }
  }, [sessionQuery.error]);

  const status: AuthStatus = !hasSession
    ? 'unauthenticated'
    : sessionQuery.isSuccess
      ? 'authenticated'
      : sessionQuery.isError
        ? 'unauthenticated'
        : 'loading';

  const login = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const result = await authApi.login(email, password);
      if (isMfaChallenge(result)) {
        return { mfaRequired: true, mfaChallengeToken: result.mfa_challenge_token };
      }
      tokenStore.setTokens(result.access_token, result.refresh_token);
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      return { mfaRequired: false };
    },
    [queryClient],
  );

  const verifyMfa = useCallback(
    async (mfaChallengeToken: string, code: string) => {
      const pair = await authApi.verifyMfa(mfaChallengeToken, code);
      tokenStore.setTokens(pair.access_token, pair.refresh_token);
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* best-effort — revoke server-side if reachable, but always clear locally */
    }
    tokenStore.clear();
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => {
    const session = sessionQuery.data ?? null;
    return {
      status,
      session,
      permissions: session ? new Permissions(session) : null,
      login,
      verifyMfa,
      logout,
      refresh,
    };
  }, [status, sessionQuery.data, login, verifyMfa, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
